import type { AiGenerationT, Job } from "@malfak/contracts";
import { toast } from "sonner";
import { api } from "@/lib/api/client";
import { queryClient } from "@/lib/api/query-client";
import { i18n } from "@/lib/i18n";
import { isReauth, reauthToast, sendErrorInfo } from "@/lib/reauth";
import { isTerminal, jobKindLabel, jobLabel, refreshJobsSoon, type JobEvent } from "./model";

/**
 * Start/finish toasts for jobs (ALP-67). A job gets a start toast when the user starts it (`announceJob`) or when its first
 * SSE event says "queued" (started elsewhere, e.g. another tab or the Copilot); only those jobs get a finish toast, so
 * background jobs (seeded/running before the page opened, local jobs that finish instantly) never spam.
 */
const announced = new Map<string, string>();
const finished = new Set<string>();
/** ai.* jobs (every AI generation is a job): their kind decides the "ready" toast + where View goes. */
const aiKinds = new Map<string, string>();
const isAiKind = (k: string | undefined | null) => !!k && k.startsWith("ai.");
/** AI jobs whose page is open and shows the result in place: no "ready" toast for text results (images always toast). */
const watched = new Map<string, number>();
export function watchAiJob(id: string): () => void {
  watched.set(id, (watched.get(id) ?? 0) + 1);
  return () => {
    const n = (watched.get(id) ?? 1) - 1;
    if (n <= 0) watched.delete(id);
    else watched.set(id, n);
  };
}

let navigateTo: (href: string) => void = (href) => window.location.assign(href);
/** The app shell registers the router's navigate so toast actions don't reload the page. */
export function registerJobNavigator(fn: (href: string) => void) {
  navigateTo = fn;
}

const t = i18n.t.bind(i18n);

function cachedLabel(id: string, kind?: string): string {
  for (const [, data] of queryClient.getQueriesData<Job | { items: Job[] }>({ queryKey: ["job"] })) {
    if (data && "id" in data && data.id === id) return jobLabel(data, t);
  }
  for (const [, data] of queryClient.getQueriesData<{ items: Job[] }>({ queryKey: ["jobs"] })) {
    const hit = data?.items?.find((j) => j.id === id);
    if (hit) return jobLabel(hit, t);
  }
  return kind ? jobKindLabel(kind, t) : t("jobs.title");
}

const toastId = (id: string) => `job-${id}`;

/**
 * QA F12: a reversible job (inbox bulk hide/archive…) gets its own wording and an Undo button on the start toast (undo is
 * cancel-safe server-side) and on the success toast. Time-limited: the button lives as long as the toast.
 */
export interface UndoableJob {
  running: string;
  done: string;
  undoLabel: string;
  onUndo: () => void;
}
const undoable = new Map<string, UndoableJob>();
export const UNDO_TOAST_MS = 8000;

/** Register a job the user just started: start toast + tray refresh; the finish toast arrives via SSE. */
export function announceJob(job: Job, opts: { undo?: UndoableJob } = {}): void {
  refreshJobsSoon(queryClient, job.id, 50);
  if (announced.has(job.id)) return;
  const label = jobLabel(job, t);
  announced.set(job.id, label);
  if (opts.undo) undoable.set(job.id, opts.undo);
  if (isTerminal(job.status)) return finish(job.id, job.status, job.failed, job.errorCode);
  const view = { label: t("jobs.actions.view"), onClick: () => navigateTo(`/jobs?job=${job.id}`) };
  if (opts.undo) {
    const u = opts.undo;
    toast.loading(u.running, { id: toastId(job.id), duration: UNDO_TOAST_MS, action: { label: u.undoLabel, onClick: () => undoNow(job.id) }, cancel: view });
    return;
  }
  toast(t("jobs.toast.started", { title: label }), {
    id: toastId(job.id),
    description: t("jobs.toast.startedHint"),
    duration: 4500,
    action: view,
  });
}

/** Undo once (a second click, or the toast of the other phase, can't start a second undo). */
function undoNow(id: string) {
  const u = undoable.get(id);
  if (!u) return;
  undoable.delete(id);
  toast.dismiss(toastId(id));
  u.onUndo();
}

/** Where "View" goes for an AI job: image results open in AI Studio, everything else in the Jobs center. */
export function aiResultHref(id: string, kind: string): string {
  return kind === "ai.image" || kind === "ai.image_edit" ? `/studio?tab=images&gen=${id}` : `/jobs?job=${id}`;
}

/**
 * Register an AI job the user just started (in-place progress shows it, so no start toast). When it finishes — even after
 * the user left the page — "Your image is ready" (View opens the result) or the typed failure reason with Retry.
 */
export function announceAiJob(g: Pick<AiGenerationT, "jobId" | "errorCode" | "error"> & { kind: string; status: string }): void {
  refreshJobsSoon(queryClient, g.jobId, 50);
  aiKinds.set(g.jobId, g.kind);
  if (!announced.has(g.jobId)) announced.set(g.jobId, jobKindLabel(g.kind, t));
  if (isTerminal(g.status)) finish(g.jobId, g.status, 0, g.errorCode, g.error);
}

function aiFinish(id: string, kind: string, status: string, errorCode?: string | null, error?: string | null) {
  for (const k of ["aiGenerations", "aiGeneration", "media", "notifications"]) void queryClient.invalidateQueries({ queryKey: [k] });
  const view = { label: t("jobs.ai.view"), onClick: () => navigateTo(aiResultHref(id, kind)) };
  if (status === "succeeded") {
    if (watched.has(id) && kind !== "ai.image" && kind !== "ai.image_edit") return;
    const title = kind === "ai.image_edit" ? t("jobs.ai.ready.edit") : kind === "ai.image" ? t("jobs.ai.ready.image") : t("jobs.ai.ready.text");
    toast.success(title, { id: toastId(id), duration: 8000, action: view });
    return;
  }
  // the page that started it shows the failure in place (with Retry): toast only when the user left it
  if (watched.has(id)) return;
  const label = announced.get(id) ?? jobKindLabel(kind, t);
  const retry = {
    label: t("jobs.ai.retry"),
    onClick: () => {
      void api("retryJob", { params: { jobId: id }, body: {} })
        .then((j) => announceAiJob({ jobId: j.id, kind: j.kind, status: j.status, errorCode: j.errorCode ?? null, error: j.error }))
        .catch(() => toast.error(t("jobs.conflict")));
    },
  };
  if (status === "cancelled") return void toast(t("jobs.toast.cancelled", { title: label }), { id: toastId(id), duration: 6000, action: retry });
  toast.error(t("jobs.ai.failedTitle", { title: label }), { id: toastId(id), description: sendErrorInfo({ errorCode, error }).message, duration: 10_000, action: retry, cancel: { label: t("jobs.actions.view"), onClick: () => navigateTo(`/jobs?job=${id}`) } });
}

function finish(id: string, status: string, failed = 0, errorCode?: string | null, error?: string | null) {
  if (finished.has(id)) return;
  finished.add(id);
  const aiKind = aiKinds.get(id);
  if (aiKind) return aiFinish(id, aiKind, status, errorCode, error);
  // the channel token is gone: say so and offer Reconnect (retrying would fail the same way)
  if (status === "failed" && isReauth(errorCode)) return reauthToast({ id: toastId(id) });
  const title = announced.get(id) ?? cachedLabel(id);
  const view = { label: t("jobs.actions.view"), onClick: () => navigateTo(`/jobs?job=${id}`) };
  const u = undoable.get(id);
  if (u && status === "succeeded") {
    toast.success(u.done, { id: toastId(id), duration: UNDO_TOAST_MS, description: failed ? t("jobs.toast.partial", { count: failed }) : undefined, action: { label: u.undoLabel, onClick: () => undoNow(id) }, cancel: view });
    setTimeout(() => undoable.delete(id), UNDO_TOAST_MS);
    return;
  }
  undoable.delete(id);
  if (status === "succeeded" && !failed) {
    toast.success(t("jobs.toast.succeeded", { title }), { id: toastId(id), duration: 5000, action: view });
    return;
  }
  const retry = {
    label: t("jobs.actions.retry"),
    onClick: () => {
      void api("retryJob", { params: { jobId: id }, body: {} })
        .then((j) => announceJob(j))
        .catch(() => toast.error(t("jobs.conflict")));
    },
  };
  if (status === "cancelled") toast(t("jobs.toast.cancelled", { title }), { id: toastId(id), duration: 6000, action: retry });
  else if (status === "succeeded") toast.warning(t("jobs.toast.succeeded", { title }), { id: toastId(id), description: t("jobs.toast.partial", { count: failed }), duration: 8000, action: retry, cancel: view });
  else toast.error(t("jobs.toast.failed", { title }), { id: toastId(id), description: failed ? t("jobs.toast.partial", { count: failed }) : undefined, duration: 8000, action: retry, cancel: view });
}

/** Called for every `operation.updated` event. `tracked` = an older trackOperation toast already covers it. */
export function onJobEvent(e: JobEvent, opts: { tracked: boolean }) {
  if (opts.tracked) return;
  if (isAiKind(e.kind) && !aiKinds.has(e.operationId)) aiKinds.set(e.operationId, e.kind!);
  if (!announced.has(e.operationId)) {
    if (e.status !== "queued") return;
    const label = cachedLabel(e.operationId, e.kind);
    announced.set(e.operationId, label);
    toast(t("jobs.toast.started", { title: label }), { id: toastId(e.operationId), description: t("jobs.toast.startedHint"), duration: 4500 });
    return;
  }
  if (isTerminal(e.status)) finish(e.operationId, e.status, e.failed ?? 0, e.errorCode, e.error);
}

/** Test helper. */
export function resetJobToasts() {
  announced.clear();
  finished.clear();
  undoable.clear();
  aiKinds.clear();
  watched.clear();
}
